import { useState, useCallback, useRef, useEffect } from 'react';
import {
  useUploadMigrationFile,
  useMapFields,
  useValidateMigration,
  useExecuteMigration,
  useMigrationStatus,
  useMigrationReport,
  useDownloadTemplate,
  useMigrationList,
  useDownloadRolesTemplate,
  useImportRolesPermissions,
  useDownloadUsersTeamsTemplate,
  useImportUsersTeams,
  usePreviewUsersTeams,
  MIGRATION_ENTITY_TYPES,
} from './hooks/useMigration';
import type {
  UploadResponse,
  FieldMapping,
  MigrationEntityType,
  MigrationImport,
  MigrationStatus,
  RolesImportReport,
  UsersTeamsImportReport,
  UsersTeamsPreview,
} from './hooks/useMigration';
import { usePermissions, PERMISSIONS } from '@/hooks/usePermissions';
import { useEntityTypes } from '@/features/settings/hooks/useEntityTypes';
import { useJobEntityTypes } from '@/features/jobs/hooks/useJobEntityTypes';
import {
  useResolveFormForEntityType,
  type FormField,
} from '@/features/form-builder/hooks/useFormBuilder';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

/** Wizard step identifiers. */
type WizardStep = 'upload' | 'mapping' | 'validation' | 'execution' | 'report';

/** Sentinel select value for "map to a custom form-builder field". */
const CUSTOM_FIELD_SENTINEL = '__custom__';

/** Entities whose forms are dynamic (form-builder driven) and support form_data. */
const FORM_BACKED_ENTITIES: MigrationEntityType[] = ['clients', 'contacts', 'jobs'];

function isFormBacked(entity: MigrationEntityType): boolean {
  return FORM_BACKED_ENTITIES.includes(entity);
}

/**
 * UI-only sentinel for the Roles & Permissions importer. It appears in the
 * "Data type" dropdown alongside the row-per-record entities, but it uses a
 * different (pivoted matrix) format and a synchronous upload→report flow — so
 * it is NOT part of the backend MIGRATION_ENTITY_TYPES enum, and the wizard
 * branches to a dedicated panel when it's selected.
 */
const ROLES_PERMISSIONS_TYPE = 'roles_permissions';

/**
 * UI-only sentinel for the combined Users-with-Teams importer. Like the roles
 * importer, it's a dedicated upload→report flow (no field mapping) rather than
 * a backend MIGRATION_ENTITY_TYPES entry.
 */
const USERS_TEAMS_TYPE = 'users_teams';

/** A value in the Data type dropdown: a real entity type, or a special importer. */
type DataTypeValue = MigrationEntityType | typeof ROLES_PERMISSIONS_TYPE | typeof USERS_TEAMS_TYPE;

/** A mappable target field shown in the mapping step. */
interface TargetFieldOption {
  name: string;
  label: string;
}

/**
 * Flattens a resolved form definition into mappable target fields. Each form
 * field contributes its `field_key` (the key the backend stores in form_data /
 * writes to a real column for system fields) with a human label. System fields
 * are marked so users understand they land in real columns.
 */
function targetFieldsFromForm(
  sections: { fields: FormField[] }[] | undefined,
): TargetFieldOption[] {
  if (!sections) return [];
  const out: TargetFieldOption[] = [];
  const seen = new Set<string>();
  for (const section of sections) {
    for (const field of section.fields ?? []) {
      if (!field.is_active) continue;
      if (seen.has(field.field_key)) continue;
      seen.add(field.field_key);
      out.push({
        name: field.field_key,
        label: field.is_system_field ? field.label : `${field.label} (custom)`,
      });
    }
  }
  return out;
}

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'upload', label: 'Upload File' },
  { key: 'mapping', label: 'Map Fields' },
  { key: 'validation', label: 'Validate' },
  { key: 'execution', label: 'Execute' },
  { key: 'report', label: 'Report' },
];

/** Normalizes a header/field for fuzzy comparison: lowercased alphanumerics. */
function normalizeKey(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '');
}

/** Splits a header/field into lowercase word tokens (by non-alphanumerics). */
function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * Finds the best matching target field for a source CSV column header.
 *
 * Matching is layered, best-first:
 *  1. Exact match on normalized field name or label.
 *  2. One string contained in the other (e.g. "Client Email" ↔ "email").
 *  3. Token overlap (e.g. "First Name" ↔ "first_name").
 *
 * Returns the target field `name`, or '' when nothing is confidently similar
 * (so the user maps it manually). `taken` excludes targets already assigned to
 * another column, so two columns don't auto-map to the same field.
 */
function autoMatchTarget(
  sourceColumn: string,
  targetFields: { name: string; label: string }[],
  taken: Set<string>,
): string {
  const srcNorm = normalizeKey(sourceColumn);
  const srcTokens = new Set(tokenize(sourceColumn));
  if (!srcNorm) return '';

  let best = '';
  let bestScore = 0;

  for (const field of targetFields) {
    if (!field.name || taken.has(field.name)) continue;

    const nameNorm = normalizeKey(field.name);
    // Strip a trailing "(custom)" marker from labels before comparing.
    const labelNorm = normalizeKey(field.label.replace(/\(custom\)$/i, ''));

    let score = 0;
    if (srcNorm === nameNorm || srcNorm === labelNorm) {
      score = 100; // exact
    } else if (
      nameNorm.includes(srcNorm) ||
      srcNorm.includes(nameNorm) ||
      (labelNorm && (labelNorm.includes(srcNorm) || srcNorm.includes(labelNorm)))
    ) {
      score = 70; // substring
    } else {
      // Token overlap ratio against the field name's tokens.
      const fieldTokens = tokenize(field.name);
      const overlap = fieldTokens.filter((t) => srcTokens.has(t)).length;
      if (overlap > 0) {
        score = 40 + Math.round((overlap / Math.max(fieldTokens.length, srcTokens.size)) * 20);
      }
    }

    if (score > bestScore) {
      bestScore = score;
      best = field.name;
    }
  }

  // Require a minimum confidence so weak guesses are left for manual mapping.
  return bestScore >= 40 ? best : '';
}

/** Human-friendly labels for the entity types the importer supports. */
const ENTITY_TYPE_LABELS: Record<MigrationEntityType, string> = {
  clients: 'Clients',
  contacts: 'Contacts',
  users: 'Users',
  teams: 'Teams',
  jobs: 'Jobs',
  invoices: 'Invoices',
  payments: 'Payments',
  credit_notes: 'Credit Notes',
  time_entries: 'Time Entries',
};

/**
 * Data type dropdown options: the row-per-record entities plus the pivoted
 * Roles & Permissions importer. Declared AFTER ENTITY_TYPE_LABELS so it reads
 * an already-initialized value (avoids a temporal-dead-zone ReferenceError).
 */
/**
 * Standalone entity types hidden from the Data type dropdown because a
 * dedicated importer supersedes them. Users and Teams are covered by the
 * combined "Users with Teams" flow, so we don't offer them individually.
 * (The backend still supports these entity types; they're just not surfaced.)
 */
const HIDDEN_ENTITY_TYPES: MigrationEntityType[] = ['users', 'teams'];

const DATA_TYPE_OPTIONS: { value: DataTypeValue; label: string }[] = [
  ...MIGRATION_ENTITY_TYPES.filter((t) => !HIDDEN_ENTITY_TYPES.includes(t)).map((t) => ({
    value: t as DataTypeValue,
    label: ENTITY_TYPE_LABELS[t],
  })),
  { value: ROLES_PERMISSIONS_TYPE, label: 'Roles & Permissions' },
  { value: USERS_TEAMS_TYPE, label: 'Users with Teams' },
];

/**
 * Human label for any entity_type value seen in History — including the special
 * synchronous importers (which aren't in ENTITY_TYPE_LABELS). Falls back to the
 * raw value so unknown types still render.
 */
function entityTypeLabel(entityType: string): string {
  const special: Record<string, string> = {
    [ROLES_PERMISSIONS_TYPE]: 'Roles & Permissions',
    [USERS_TEAMS_TYPE]: 'Users with Teams',
  };
  return special[entityType] ?? ENTITY_TYPE_LABELS[entityType as MigrationEntityType] ?? entityType;
}

/**
 * Target fields available for mapping, per entity type. Field names are the
 * REAL target-table columns (see tenant-schema.sql), except for a few friendly
 * aliases the backend worker normalizes (amount→total, date→issue_date/entry_date,
 * hours→duration_minutes). Keeping the friendly labels avoids exposing internal
 * column names to end users while still landing in valid columns.
 */
const TARGET_FIELDS_BY_ENTITY: Record<MigrationEntityType, { name: string; label: string }[]> = {
  clients: [
    { name: 'business_name', label: 'Business Name' },
    { name: 'first_name', label: 'First Name' },
    { name: 'last_name', label: 'Last Name' },
    { name: 'email', label: 'Email' },
    { name: 'phone', label: 'Phone' },
    { name: 'business_number', label: 'Business Number' },
    { name: 'address_line1', label: 'Address Line 1' },
    { name: 'city', label: 'City' },
    { name: 'province', label: 'Province' },
    { name: 'postal_code', label: 'Postal Code' },
    { name: 'assigned_partner_id', label: 'Assigned Partner (email)' },
    { name: 'assigned_manager_id', label: 'Assigned Manager (email)' },
    // entity_type_id is set automatically from the Entity type chosen at
    // upload — it's applied to every imported row, so it isn't a mappable
    // column here.
  ],
  contacts: [
    { name: 'first_name', label: 'First Name' },
    { name: 'last_name', label: 'Last Name' },
    { name: 'email', label: 'Email' },
    { name: 'phone', label: 'Phone' },
    { name: 'business_name', label: 'Business Name' },
    { name: 'date_of_birth', label: 'Date of Birth' },
    { name: 'assigned_partner_id', label: 'Assigned Partner (email)' },
    { name: 'assigned_manager_id', label: 'Assigned Manager (email)' },
    // entity_type_id is set automatically from the Entity type chosen at upload.
  ],
  users: [
    { name: 'email', label: 'Email' },
    { name: 'name', label: 'Full Name' },
    { name: 'role_id', label: 'Role (name)' },
    { name: 'job_role', label: 'Job Role' },
    { name: 'department', label: 'Department' },
    { name: 'hourly_rate', label: 'Hourly Rate' },
    { name: 'status', label: 'Status' },
  ],
  teams: [
    { name: 'name', label: 'Team Name' },
    { name: 'description', label: 'Description' },
    { name: 'lead_user_id', label: 'Team Lead (email)' },
  ],
  jobs: [
    { name: 'title', label: 'Title' },
    { name: 'client_id', label: 'Client ID' },
    { name: 'job_type', label: 'Job Type' },
    { name: 'job_code', label: 'Job Code' },
    { name: 'assigned_partner_id', label: 'Assigned Partner (email)' },
    { name: 'assigned_manager_id', label: 'Assigned Manager (email)' },
    { name: 'assigned_accountant_id', label: 'Assigned Accountant (email)' },
    // entity_type_id (the job type) is set automatically from the Job type
    // chosen at upload — applied to every imported job.
  ],
  invoices: [
    { name: 'invoice_number', label: 'Invoice Number' },
    { name: 'client_id', label: 'Client ID' },
    { name: 'total', label: 'Amount' },
    { name: 'issue_date', label: 'Issue Date' },
    { name: 'due_date', label: 'Due Date' },
  ],
  payments: [
    { name: 'invoice_id', label: 'Invoice ID' },
    { name: 'amount', label: 'Amount' },
    { name: 'payment_date', label: 'Payment Date' },
    { name: 'payment_method', label: 'Payment Method' },
    { name: 'reference_number', label: 'Reference Number' },
  ],
  credit_notes: [
    { name: 'credit_note_number', label: 'Credit Note Number' },
    { name: 'invoice_id', label: 'Invoice ID' },
    { name: 'client_id', label: 'Client ID' },
    { name: 'amount', label: 'Amount' },
    { name: 'reason', label: 'Reason' },
  ],
  time_entries: [
    { name: 'practice_entity_id', label: 'Practice Entity ID' },
    { name: 'job_id', label: 'Job ID' },
    { name: 'user_id', label: 'User ID' },
    { name: 'duration_minutes', label: 'Duration (minutes)' },
    { name: 'entry_date', label: 'Entry Date' },
  ],
};

/**
 * Data Migration Wizard — multi-step wizard for importing data from CCH iFirm.
 * Steps: upload file → map fields → validate → execute → review report.
 *
 * Access is gated by the migration.read permission; write actions (upload,
 * map, validate, execute) additionally require migration.write.
 *
 * Validates: Requirements 38.1, 38.2, 38.3
 */
export function MigrationWizard() {
  const { can, isLoading: permsLoading } = usePermissions();
  const canRead = can(PERMISSIONS.MIGRATION_READ);
  const canWrite = can(PERMISSIONS.MIGRATION_WRITE);

  const [view, setView] = useState<'import' | 'history'>('import');
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [migrationId, setMigrationId] = useState<string | null>(null);
  const [entityType, setEntityType] = useState<DataTypeValue>('contacts');
  const isRolesImport = entityType === ROLES_PERMISSIONS_TYPE;
  const isUsersTeamsImport = entityType === USERS_TEAMS_TYPE;
  // Both special importers use a dedicated upload→report panel (no wizard steps).
  const isSpecialImport = isRolesImport || isUsersTeamsImport;
  const [entityTypeId, setEntityTypeId] = useState<string>('');
  const [uploadData, setUploadData] = useState<UploadResponse | null>(null);
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [isExecuting, setIsExecuting] = useState(false);

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  function goToStep(step: WizardStep) {
    setCurrentStep(step);
  }

  if (permsLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
        role="status"
      >
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        Loading...
      </div>
    );
  }

  if (!canRead) {
    return (
      <div
        className="rounded-lg bg-warning-50 p-4 text-sm text-warning-700 dark:bg-yellow-900/30 dark:text-yellow-300"
        role="alert"
      >
        You do not have permission to access data migration. Contact your administrator if you need
        access.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Data Migration</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Import data from CCH iFirm or other systems into the platform.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!canWrite && <Badge variant="info">Read-only access</Badge>}
          {/* View toggle: New Import wizard vs. History of past imports. */}
          <div
            className="inline-flex rounded-lg border border-border p-0.5 dark:border-border-dark"
            role="tablist"
            aria-label="Migration view"
          >
            <button
              type="button"
              role="tab"
              aria-selected={view === 'import'}
              onClick={() => setView('import')}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'import'
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100',
              )}
              style={
                view === 'import'
                  ? { backgroundColor: 'var(--backoffice-secondary-600)' }
                  : undefined
              }
            >
              New Import
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'history'}
              onClick={() => setView('history')}
              className={cn(
                'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                view === 'history'
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100',
              )}
              style={
                view === 'history'
                  ? { backgroundColor: 'var(--backoffice-secondary-600)' }
                  : undefined
              }
            >
              History
            </button>
          </div>
        </div>
      </div>

      {view === 'history' && <MigrationHistory />}

      {/* Step indicator + wizard (New Import view) */}
      {view === 'import' && (
        <>
          {/* The 5-step indicator only applies to row-per-record imports. The
              special importers (Roles & Permissions, Users with Teams) are
              single upload→report panels. */}
          {!isSpecialImport && (
            <nav aria-label="Migration progress">
              <ol className="flex flex-wrap items-center gap-2">
                {STEPS.map((step, idx) => (
                  <li key={step.key} className="flex items-center">
                    <span
                      className={cn(
                        'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                        idx < currentStepIndex
                          ? 'bg-success-500 text-white'
                          : idx === currentStepIndex
                            ? 'bg-brand-600 text-white'
                            : 'bg-surface-tertiary text-gray-600 dark:bg-surface-dark-tertiary dark:text-gray-300',
                      )}
                      aria-current={idx === currentStepIndex ? 'step' : undefined}
                    >
                      {idx < currentStepIndex ? '✓' : idx + 1}
                    </span>
                    <span
                      className={cn(
                        'ml-2 text-sm',
                        idx === currentStepIndex
                          ? 'font-medium text-gray-900 dark:text-gray-100'
                          : 'text-gray-500 dark:text-gray-400',
                      )}
                    >
                      {step.label}
                    </span>
                    {idx < STEPS.length - 1 && (
                      <span
                        className="mx-3 h-px w-8 bg-border dark:bg-border-dark"
                        aria-hidden="true"
                      />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          )}

          {/* Step content */}
          <Card>
            {/* Special importers: dedicated upload→report flows (no field
                mapping). The data-type selector stays visible so the user can
                switch back to a row-per-record import. */}
            {isSpecialImport && (
              <div className="space-y-6">
                <Select
                  label="Data type"
                  value={entityType}
                  onChange={(e) => {
                    setEntityType(e.target.value as DataTypeValue);
                    setEntityTypeId('');
                  }}
                  disabled={!canWrite}
                  className="sm:max-w-xs"
                  options={DATA_TYPE_OPTIONS}
                />
                {isRolesImport && <RolesPermissionsImport canWrite={canWrite} />}
                {isUsersTeamsImport && <UsersTeamsImport canWrite={canWrite} />}
              </div>
            )}

            {!isSpecialImport && currentStep === 'upload' && (
              <UploadStep
                entityType={entityType as MigrationEntityType}
                onEntityTypeChange={(v) => setEntityType(v)}
                entityTypeId={entityTypeId}
                onEntityTypeIdChange={setEntityTypeId}
                canWrite={canWrite}
                onUploaded={(data) => {
                  setUploadData(data);
                  setMigrationId(data.id);
                  setEntityType(data.entity_type);
                  if (data.entity_type_id) setEntityTypeId(data.entity_type_id);
                  setMappings(data.columns.map((col) => ({ sourceColumn: col, targetField: '' })));
                  goToStep('mapping');
                }}
              />
            )}

            {!isSpecialImport && currentStep === 'mapping' && uploadData && (
              <MappingStep
                uploadData={uploadData}
                entityType={entityType as MigrationEntityType}
                entityTypeId={entityTypeId}
                mappings={mappings}
                onMappingsChange={setMappings}
                migrationId={migrationId!}
                canWrite={canWrite}
                onMapped={() => goToStep('validation')}
                onBack={() => goToStep('upload')}
              />
            )}

            {currentStep === 'validation' && migrationId && (
              <ValidationStep
                migrationId={migrationId}
                canWrite={canWrite}
                onNext={() => {
                  setIsExecuting(true);
                  goToStep('execution');
                }}
                onBack={() => goToStep('mapping')}
              />
            )}

            {currentStep === 'execution' && migrationId && (
              <ExecutionStep
                migrationId={migrationId}
                isExecuting={isExecuting}
                onComplete={() => {
                  setIsExecuting(false);
                  goToStep('report');
                }}
              />
            )}

            {currentStep === 'report' && migrationId && <ReportStep migrationId={migrationId} />}
          </Card>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Upload Step
// ---------------------------------------------------------------------------

interface UploadStepProps {
  entityType: MigrationEntityType;
  /** May switch to the roles sentinel; the parent branches to the roles panel. */
  onEntityTypeChange: (value: DataTypeValue) => void;
  entityTypeId: string;
  onEntityTypeIdChange: (value: string) => void;
  canWrite: boolean;
  onUploaded: (data: UploadResponse) => void;
}

function UploadStep({
  entityType,
  onEntityTypeChange,
  entityTypeId,
  onEntityTypeIdChange,
  canWrite,
  onUploaded,
}: UploadStepProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadMigrationFile();
  const templateMutation = useDownloadTemplate();

  const formBacked = isFormBacked(entityType);
  // Form-backed imports require a specific entity type / job type first so the
  // dynamic form (and its custom fields) can be resolved.
  const canUpload = canWrite && (!formBacked || !!entityTypeId);
  // Contact entity types drive clients/contacts forms; job types drive jobs.
  const { data: contactEntityTypes } = useEntityTypes();
  const { data: jobEntityTypes } = useJobEntityTypes();
  const typeOptions = (entityType === 'jobs' ? (jobEntityTypes ?? []) : (contactEntityTypes ?? []))
    .filter((t) => t.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((t) => ({ id: t.id, name: t.name }));

  const handleFile = useCallback(
    (file: File) => {
      uploadMutation.mutate(
        { file, entityType, entityTypeId: entityTypeId || undefined },
        { onSuccess: (data) => onUploaded(data) },
      );
    },
    [uploadMutation, entityType, entityTypeId, onUploaded],
  );

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (!canUpload) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(true);
  }

  function handleDragLeave() {
    setIsDragOver(false);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Upload Migration File
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Choose what you are importing, then upload a CSV file exported from your previous system.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 sm:max-w-xl">
        {/* Data type */}
        <Select
          label="Data type"
          value={entityType}
          onChange={(e) => {
            onEntityTypeChange(e.target.value as DataTypeValue);
            // Reset the specific type when the data type changes.
            onEntityTypeIdChange('');
          }}
          disabled={!canWrite}
          options={DATA_TYPE_OPTIONS}
        />

        {/* Dynamic entity type / job type — drives which form (and custom fields) apply */}
        {formBacked && (
          <Select
            label={entityType === 'jobs' ? 'Job type' : 'Entity type'}
            value={entityTypeId}
            onChange={(e) => onEntityTypeIdChange(e.target.value)}
            disabled={!canWrite}
            placeholder={entityType === 'jobs' ? 'Select a job type…' : 'Select an entity type…'}
            hint="Determines the custom fields available for mapping."
            options={typeOptions.map((t) => ({ value: t.id, label: t.name }))}
          />
        )}
      </div>

      {/* Download a pre-columned CSV template for the selected type. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          type="button"
          loading={templateMutation.isPending}
          disabled={formBacked && !entityTypeId}
          onClick={() =>
            templateMutation.mutate({ entityType, entityTypeId: entityTypeId || undefined })
          }
          leftIcon={
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
              />
            </svg>
          }
        >
          {templateMutation.isPending ? 'Preparing…' : 'Download CSV template'}
        </Button>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Get a pre-columned file to fill in
          {formBacked ? ', including this type’s custom fields' : ''}.
        </span>
      </div>

      {templateMutation.isError && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Could not generate template: {templateMutation.error?.message ?? 'Unknown error'}
        </div>
      )}

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => canUpload && fileInputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-disabled={!canUpload}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && canUpload) fileInputRef.current?.click();
        }}
        aria-label="Drop zone for file upload"
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-12 transition-colors',
          !canUpload
            ? 'cursor-not-allowed border-border bg-surface-secondary opacity-60 dark:border-border-dark dark:bg-surface-dark-tertiary'
            : isDragOver
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
              : 'border-border hover:border-brand-400 dark:border-border-dark',
        )}
      >
        <svg
          className="mb-3 h-10 w-10 text-gray-400 dark:text-gray-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
          />
        </svg>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <span className="font-medium text-brand-600 dark:text-brand-400">Click to upload</span> or
          drag and drop
        </p>
        <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">CSV (max 10 MB)</p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFileSelect}
        disabled={!canUpload}
        className="hidden"
        aria-hidden="true"
      />

      {formBacked && !entityTypeId && (
        <p className="text-xs text-warning-700 dark:text-yellow-300" role="note">
          Select {entityType === 'jobs' ? 'a job type' : 'an entity type'} above to enable upload.
        </p>
      )}

      {uploadMutation.isPending && (
        <div
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          Uploading file...
        </div>
      )}

      {uploadMutation.isError && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Upload failed: {uploadMutation.error?.message ?? 'Unknown error'}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mapping Step
// ---------------------------------------------------------------------------

interface MappingStepProps {
  uploadData: UploadResponse;
  entityType: MigrationEntityType;
  entityTypeId: string;
  mappings: FieldMapping[];
  onMappingsChange: (mappings: FieldMapping[]) => void;
  migrationId: string;
  canWrite: boolean;
  onMapped: () => void;
  onBack: () => void;
}

function MappingStep({
  uploadData,
  entityType,
  entityTypeId,
  mappings,
  onMappingsChange,
  migrationId,
  canWrite,
  onMapped,
  onBack,
}: MappingStepProps) {
  const mapFieldsMutation = useMapFields();

  // Entities backed by a `form_data` JSONB column support mapping to custom
  // (form-builder) fields in addition to the known real columns.
  const supportsCustomFields = isFormBacked(entityType);

  // Resolve the actual form definition for the chosen entity type / job type
  // and derive the mappable target fields from it, so the mapping options are
  // dynamic and match exactly what the form builder configured.
  const { data: formDefinition, isLoading: formLoading } = useResolveFormForEntityType(
    supportsCustomFields ? entityTypeId : '',
  );

  const dynamicFields = targetFieldsFromForm(formDefinition?.sections);

  // The static list is the baseline (real system columns). When a form
  // definition is available, its fields extend/override the baseline so
  // form-builder-configured fields (including custom ones) are mappable.
  const baseFields = TARGET_FIELDS_BY_ENTITY[entityType];
  const mergedByKey = new Map<string, { name: string; label: string }>();
  for (const f of baseFields) mergedByKey.set(f.name, f);
  for (const f of dynamicFields) mergedByKey.set(f.name, f);
  const knownFields = Array.from(mergedByKey.values());
  const knownFieldNames = new Set(knownFields.map((f) => f.name));

  const targetFields = [
    { name: '', label: '— Skip —' },
    ...knownFields,
    ...(supportsCustomFields ? [{ name: CUSTOM_FIELD_SENTINEL, label: 'Custom field…' }] : []),
  ];

  // Auto-map columns whose header matches a target field, so the user only has
  // to handle the ones we couldn't confidently identify. Runs once the target
  // fields are known (including async-loaded form fields) and never overrides a
  // choice the user has already made — it only fills still-empty targets.
  const autoMappedRef = useRef(false);
  useEffect(() => {
    // Wait until form fields (if any) have resolved so matches consider them.
    if (supportsCustomFields && formLoading) return;
    if (autoMappedRef.current) return;
    if (mappings.length === 0) return;

    // Only auto-map if nothing has been mapped yet (fresh arrival on the step).
    if (mappings.some((m) => m.targetField)) {
      autoMappedRef.current = true;
      return;
    }

    const taken = new Set<string>();
    let changed = false;
    const next = mappings.map((m) => {
      const match = autoMatchTarget(m.sourceColumn, knownFields, taken);
      if (match) {
        taken.add(match);
        changed = true;
        return { ...m, targetField: match, isCustom: false };
      }
      return m;
    });

    autoMappedRef.current = true;
    if (changed) onMappingsChange(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportsCustomFields, formLoading, knownFields.length]);

  /** A mapping is "custom" when its target isn't empty and isn't a known column. */
  function isCustom(targetField: string): boolean {
    return targetField !== '' && !knownFieldNames.has(targetField);
  }

  function handleMappingChange(index: number, targetField: string) {
    const updated = [...mappings];
    // Selecting "Custom field…" clears the target so the user types a key.
    updated[index] = {
      ...updated[index],
      targetField: targetField === CUSTOM_FIELD_SENTINEL ? '' : targetField,
      isCustom: targetField === CUSTOM_FIELD_SENTINEL,
    };
    onMappingsChange(updated);
  }

  function handleCustomKeyChange(index: number, key: string) {
    const updated = [...mappings];
    updated[index] = { ...updated[index], targetField: key.trim(), isCustom: true };
    onMappingsChange(updated);
  }

  function handleSubmitMappings() {
    mapFieldsMutation.mutate({ id: migrationId, mappings }, { onSuccess: () => onMapped() });
  }

  const mappedCount = mappings.filter((m) => m.targetField).length;
  const unmappedCount = mappings.length - mappedCount;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Map Fields</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Map columns from your file ({uploadData.total_records} rows) to platform fields. We’ve
        auto-matched columns whose names match a field — review them and map any that are still set
        to “Skip”.
      </p>

      {mappings.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge variant="success">{mappedCount} matched</Badge>
          {unmappedCount > 0 ? (
            <Badge variant="warning">{unmappedCount} need mapping</Badge>
          ) : (
            <Badge variant="info">All columns mapped</Badge>
          )}
        </div>
      )}

      {supportsCustomFields && formLoading && (
        <div
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          Loading form fields…
        </div>
      )}

      {supportsCustomFields && !formLoading && dynamicFields.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Target fields below reflect the configured form for the selected{' '}
          {entityType === 'jobs' ? 'job type' : 'entity type'}. Unmatched columns can still be
          mapped as custom fields.
        </p>
      )}

      {supportsCustomFields && !formLoading && dynamicFields.length === 0 && (
        <p className="text-xs text-warning-700 dark:text-yellow-300" role="note">
          No form is configured for the selected{' '}
          {entityType === 'jobs' ? 'job type' : 'entity type'}. You can still map to the base fields
          or add custom fields.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-border dark:border-border-dark">
        <table
          className="min-w-full divide-y divide-border dark:divide-border-dark"
          aria-label="Field mapping"
        >
          <thead className="bg-surface-secondary dark:bg-surface-dark-tertiary">
            <tr>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
              >
                Source Column
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
              >
                Sample Data
              </th>
              <th
                scope="col"
                className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
              >
                Target Field
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
            {mappings.map((mapping, idx) => (
              <tr key={mapping.sourceColumn}>
                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {mapping.sourceColumn}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {uploadData.sample_data[0]?.[mapping.sourceColumn] ?? '—'}
                </td>
                <td className="px-4 py-3">
                  {(() => {
                    const custom = mapping.isCustom || isCustom(mapping.targetField);
                    return (
                      <div className="space-y-2">
                        <Select
                          value={custom ? CUSTOM_FIELD_SENTINEL : mapping.targetField}
                          onChange={(e) => handleMappingChange(idx, e.target.value)}
                          disabled={!canWrite}
                          aria-label={`Target field for ${mapping.sourceColumn}`}
                          options={targetFields.map((f) => ({ value: f.name, label: f.label }))}
                        />
                        {custom && (
                          <Input
                            type="text"
                            value={mapping.targetField}
                            onChange={(e) => handleCustomKeyChange(idx, e.target.value)}
                            disabled={!canWrite}
                            placeholder="custom_field_key"
                            aria-label={`Custom field key for ${mapping.sourceColumn}`}
                          />
                        )}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mapFieldsMutation.isError && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Mapping failed: {mapFieldsMutation.error?.message ?? 'Unknown error'}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="outline" type="button" onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmitMappings}
          loading={mapFieldsMutation.isPending}
          disabled={!canWrite || mappings.every((m) => !m.targetField)}
        >
          {mapFieldsMutation.isPending ? 'Saving...' : 'Continue to Validation'}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation Step
// ---------------------------------------------------------------------------

interface ValidationStepProps {
  migrationId: string;
  canWrite: boolean;
  onNext: () => void;
  onBack: () => void;
}

function ValidationStep({ migrationId, canWrite, onNext, onBack }: ValidationStepProps) {
  const validateMutation = useValidateMigration();
  const [hasTriggered, setHasTriggered] = useState(false);

  // Poll status once validation has been triggered.
  const { data: status } = useMigrationStatus(migrationId, hasTriggered);

  function handleValidate() {
    validateMutation.mutate(migrationId, { onSuccess: () => setHasTriggered(true) });
  }

  const isValidating = hasTriggered && (status?.status === 'validating' || !status);
  const isValidated = status?.status === 'validated';
  const isFailed = status?.status === 'failed';
  const report = status?.status === 'validated' || status?.status === 'failed' ? status : null;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Validate Data</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Run validation checks on mapped data before importing.
      </p>

      {!hasTriggered && (
        <Button
          type="button"
          onClick={handleValidate}
          loading={validateMutation.isPending}
          disabled={!canWrite}
        >
          {validateMutation.isPending ? 'Starting...' : 'Run Validation'}
        </Button>
      )}

      {validateMutation.isError && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Validation failed to start: {validateMutation.error?.message ?? 'Unknown error'}
        </div>
      )}

      {isValidating && (
        <div
          className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
          role="status"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          Validating records...
        </div>
      )}

      {report && (
        <div
          className={cn(
            'rounded-lg p-4',
            isValidated
              ? 'bg-success-50 dark:bg-green-900/20'
              : 'bg-warning-50 dark:bg-yellow-900/20',
          )}
          role="status"
          aria-label="Validation summary"
        >
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <Badge variant={isValidated ? 'success' : 'warning'}>
              {isValidated ? 'Validation Passed' : 'Validation Failed'}
            </Badge>
            <span className="text-gray-600 dark:text-gray-300">
              {report.success_records}/{report.total_records} rows valid
            </span>
            {report.error_records > 0 && (
              <Badge variant="danger">{report.error_records} errors</Badge>
            )}
            {report.skipped_records > 0 && (
              <Badge variant="warning">{report.skipped_records} skipped</Badge>
            )}
          </div>
        </div>
      )}

      {isFailed && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Validation reported errors. Go back to adjust your field mapping or fix your source file.
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button variant="outline" type="button" onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onNext} disabled={!canWrite || !isValidated}>
          Execute Migration
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Execution Step
// ---------------------------------------------------------------------------

interface ExecutionStepProps {
  migrationId: string;
  isExecuting: boolean;
  onComplete: () => void;
}

function ExecutionStep({ migrationId, isExecuting, onComplete }: ExecutionStepProps) {
  const executeMutation = useExecuteMigration();
  const [hasStarted, setHasStarted] = useState(false);

  const { data: status, isFetching } = useMigrationStatus(migrationId, hasStarted);

  // Start execution on mount.
  useEffect(() => {
    if (!hasStarted && isExecuting) {
      executeMutation.mutate(migrationId, { onSuccess: () => setHasStarted(true) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isTerminal = status?.status === 'completed' || status?.status === 'failed';

  // Navigate to the report once the import reaches a terminal state (completed
  // or failed). This is driven by the status polling, which keeps running
  // until the backend flips the record out of 'executing' — so hitting 100%
  // rows-processed does NOT advance on its own; only the terminal status does.
  useEffect(() => {
    if (isTerminal) {
      onComplete();
    }
  }, [isTerminal, onComplete]);

  const progress = status?.progress ?? 0;

  // The worker can report 100% of rows processed a beat before it writes the
  // final 'completed' status (validation report, timestamps, etc.). During
  // that window keep the loader up and show "Finalizing…" so the screen never
  // looks frozen at 100%.
  const isFinalizing = !isTerminal && progress >= 100;

  // While the execute request is starting or the import is still running (not
  // terminal), we're processing — keep a spinner visible the whole time.
  const isProcessing = executeMutation.isPending || (hasStarted && !isTerminal);

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Executing Migration
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Importing records into the platform. Please do not close this page.
      </p>

      {/* Processing loader — visible for the entire time the import is running,
          including the finalizing window at 100%. */}
      {isProcessing && (
        <div
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300"
          role="status"
          aria-live="polite"
        >
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
          {executeMutation.isPending
            ? 'Starting migration…'
            : isFinalizing
              ? 'Finalizing import…'
              : 'Importing records…'}
        </div>
      )}

      {/* Progress bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500 dark:text-gray-400">Progress</span>
          <span className="font-medium text-gray-900 dark:text-gray-100">{progress}%</span>
        </div>
        <div
          className="h-3 w-full overflow-hidden rounded-full bg-surface-tertiary dark:bg-surface-dark-tertiary"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Migration progress"
        >
          <div
            className={cn(
              'h-full rounded-full bg-brand-600 transition-all duration-300',
              // Subtle animation while finalizing so 100% doesn't look stuck.
              isFinalizing && 'animate-pulse',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Status details */}
      {status && (
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Rows Processed:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-100">
              {status.processed_records}/{status.total_records}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Status:</span>{' '}
            <span className="font-medium capitalize text-gray-900 dark:text-gray-100">
              {isFinalizing ? 'finalizing' : status.status}
            </span>
          </div>
        </div>
      )}

      {status?.status === 'failed' && (
        <div
          className="rounded-lg bg-danger-50 p-4 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Migration failed. Review the report for details.
        </div>
      )}

      {executeMutation.isError && (
        <div
          className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
          role="alert"
        >
          Failed to start migration: {executeMutation.error?.message ?? 'Unknown error'}
        </div>
      )}

      {/* Safety net: if polling stops advancing (e.g. worker never wrote the
          terminal status, or we hit the poll cap) the user isn't stranded —
          they can retry the status check or jump to the report manually. */}
      {hasStarted && !isTerminal && !isFetching && !isFinalizing && (
        <div className="flex items-center gap-3 pt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Still working. If this doesn’t advance shortly, you can open the report.
          </span>
          <Button type="button" variant="outline" size="sm" onClick={onComplete}>
            View report
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Report Step
// ---------------------------------------------------------------------------

interface ReportStepProps {
  migrationId: string;
}

function ReportStep({ migrationId }: ReportStepProps) {
  const { data: report, isLoading, error } = useMigrationReport(migrationId);

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
        role="status"
      >
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        Loading report...
      </div>
    );
  }

  if (error || !report) {
    return (
      <div
        className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
        role="alert"
      >
        Failed to load migration report.
      </div>
    );
  }

  // The report holds row-level entries: errors, skip reasons (e.g. duplicates),
  // and informational notes (e.g. an assignee left unassigned). They share one
  // list; the message text distinguishes them.
  const details = report.error_details ?? report.validation_report?.errors ?? [];

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Migration Report</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <SummaryCard label="Total Rows" value={report.total_records} />
        <SummaryCard label="Successful" value={report.success_records} variant="success" />
        <SummaryCard label="Errors" value={report.error_records} variant="error" />
        <SummaryCard label="Skipped" value={report.skipped_records} variant="warning" />
      </div>

      {report.completed_at && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Completed at {new Date(report.completed_at).toLocaleString()}
        </p>
      )}

      {/* Row-level details: errors, skipped reasons, and notes. */}
      {details.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Row Details ({details.length})
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Includes reasons for skipped rows (e.g. duplicates), errors, and informational notes.
          </p>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-border dark:border-border-dark">
            <table
              className="min-w-full divide-y divide-border dark:divide-border-dark"
              aria-label="Migration row details"
            >
              <thead className="sticky top-0 bg-surface-secondary dark:bg-surface-dark-tertiary">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                  >
                    Row
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                  >
                    Field
                  </th>
                  <th
                    scope="col"
                    className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                  >
                    Message
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
                {details.map((err, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {err.row}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {err.field}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                      {err.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary Card
// ---------------------------------------------------------------------------

interface SummaryCardProps {
  label: string;
  value: number;
  variant?: 'default' | 'success' | 'error' | 'warning';
}

function SummaryCard({ label, value, variant = 'default' }: SummaryCardProps) {
  const colorClasses = {
    default: 'text-gray-900 dark:text-gray-100',
    success: 'text-success-700 dark:text-green-300',
    error: 'text-danger-700 dark:text-red-300',
    warning: 'text-warning-700 dark:text-yellow-300',
  };

  return (
    <Card padding="sm">
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold', colorClasses[variant])}>
        {value.toLocaleString()}
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Migration History
// ---------------------------------------------------------------------------

/** Badge variant for each import status. */
function statusBadgeVariant(
  status: MigrationStatus,
): 'success' | 'danger' | 'warning' | 'info' | 'default' {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'validated':
      return 'info';
    case 'validating':
    case 'executing':
      return 'warning';
    default:
      return 'default';
  }
}

const HISTORY_PAGE_SIZE = 10;

/**
 * MigrationHistory — paginated list of past migration imports with a
 * drill-down to each import's report. Backed by GET /migration and reuses
 * ReportStep for the report detail.
 */
function MigrationHistory() {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, isError, error } = useMigrationList({
    page,
    pageSize: HISTORY_PAGE_SIZE,
  });

  // Report drill-down view.
  if (selectedId) {
    return (
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Import Report</h2>
          <Button variant="outline" size="sm" onClick={() => setSelectedId(null)}>
            Back to history
          </Button>
        </div>
        <ReportStep migrationId={selectedId} />
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"
        role="status"
      >
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
        Loading history...
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
        role="alert"
      >
        Failed to load migration history: {error instanceof Error ? error.message : 'Unknown error'}
      </div>
    );
  }

  const imports: MigrationImport[] = data?.imports ?? [];
  const pagination = data?.pagination;

  if (imports.length === 0) {
    return (
      <Card>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No migration imports yet. Run one from the “New Import” tab and it will appear here.
        </p>
      </Card>
    );
  }

  return (
    <Card padding="none">
      <div className="overflow-x-auto">
        <table
          className="min-w-full divide-y divide-border dark:divide-border-dark"
          aria-label="Migration history"
        >
          <thead className="bg-surface-secondary dark:bg-surface-dark-tertiary">
            <tr>
              {['Date', 'Data Type', 'File', 'Status', 'Success', 'Errors', 'Skipped', ''].map(
                (h) => (
                  <th
                    key={h}
                    scope="col"
                    className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
            {imports.map((imp) => (
              <tr
                key={imp.id}
                className="hover:bg-surface-secondary dark:hover:bg-surface-dark-tertiary"
              >
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {new Date(imp.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {entityTypeLabel(imp.entity_type)}
                </td>
                <td className="max-w-[16rem] truncate px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                  {imp.file_name ?? '—'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <Badge variant={statusBadgeVariant(imp.status)}>{imp.status}</Badge>
                </td>
                <td className="px-4 py-3 text-sm text-success-700 dark:text-green-300">
                  {imp.success_records.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-danger-700 dark:text-red-300">
                  {imp.error_records.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm text-warning-700 dark:text-yellow-300">
                  {imp.skipped_records.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedId(imp.id)}>
                    View report
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-border px-4 py-3 dark:border-border-dark">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasPrevious}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!pagination.hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Roles & Permissions Import (pivoted matrix — separate from the wizard flow)
// ---------------------------------------------------------------------------

interface RolesPermissionsImportProps {
  canWrite: boolean;
}

/**
 * Standalone Roles & Permissions importer.
 *
 * Unlike the row-per-record wizard, this consumes a PIVOTED matrix: each row is
 * a permission (label + key), each column is a role, and cells are Yes/No. It's
 * a simple upload → report flow with no field-mapping step. On success it shows
 * a per-role table of created / updated / skipped (e.g. a role name that
 * already exists as a system role is skipped and reported).
 */
function RolesPermissionsImport({ canWrite }: RolesPermissionsImportProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateMutation = useDownloadRolesTemplate();
  const importMutation = useImportRolesPermissions();
  const report: RolesImportReport | undefined = importMutation.data;

  const handleFile = useCallback(
    (file: File) => {
      importMutation.mutate(file);
    },
    [importMutation],
  );

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (!canWrite) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so re-selecting the same file still fires onChange.
    e.target.value = '';
  }

  const actionBadge = (action: RolesImportReport['results'][number]['action']) => {
    if (action === 'created') return <Badge variant="success">Created</Badge>;
    if (action === 'updated') return <Badge variant="info">Updated</Badge>;
    return <Badge variant="warning">Skipped</Badge>;
  };

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Import Roles &amp; Permissions
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Upload a permissions matrix: one row per permission, one column per role, with Yes/No
            cells. New role names are created; existing custom roles are updated; system roles are
            skipped.
          </p>
        </div>

        {/* Download the template pre-filled with every permission + example roles. */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            type="button"
            loading={templateMutation.isPending}
            onClick={() => templateMutation.mutate()}
            leftIcon={
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                />
              </svg>
            }
          >
            {templateMutation.isPending ? 'Preparing…' : 'Download CSV template'}
          </Button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Lists all available permissions with example role columns to fill in.
          </span>
        </div>

        {templateMutation.isError && (
          <div
            className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            Could not generate template: {templateMutation.error?.message ?? 'Unknown error'}
          </div>
        )}

        {/* Drop zone */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onClick={() => canWrite && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-disabled={!canWrite}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) fileInputRef.current?.click();
          }}
          className={cn(
            'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors',
            isDragOver
              ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
              : 'border-border dark:border-border-dark',
            canWrite ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelect}
            disabled={!canWrite}
          />
          <svg
            className="mb-2 h-8 w-8 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7.5 7.5h-.75A2.25 2.25 0 004.5 9.75v7.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25v-7.5a2.25 2.25 0 00-2.25-2.25h-.75m-6 3.75l3-3m0 0l3 3m-3-3v11.25"
            />
          </svg>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {importMutation.isPending
              ? 'Importing…'
              : 'Drop your filled CSV here, or click to choose a file'}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">CSV up to 10MB</p>
        </div>

        {importMutation.isError && (
          <div
            className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
            role="alert"
          >
            Import failed: {importMutation.error?.message ?? 'Unknown error'}
          </div>
        )}

        {/* Per-role report */}
        {report && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="success">{report.created} created</Badge>
              <Badge variant="info">{report.updated} updated</Badge>
              <Badge variant="warning">{report.skipped} skipped</Badge>
            </div>

            <div className="overflow-hidden rounded-lg border border-border dark:border-border-dark">
              <table className="min-w-full divide-y divide-border dark:divide-border-dark">
                <thead className="bg-surface-tertiary dark:bg-surface-dark-tertiary">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Role
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Result
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Permissions
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
                  {report.results.map((r) => (
                    <tr key={r.name}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {r.name}
                      </td>
                      <td className="px-4 py-2 text-sm">{actionBadge(r.action)}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {r.permission_count}
                        {r.data_scope ? ` · ${r.data_scope}` : ''}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        {r.message ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {report.warnings.length > 0 && (
              <div className="rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                <p className="font-medium">Warnings</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5">
                  {report.warnings.map((w, i) => (
                    <li key={i}>
                      Row {w.row}: {w.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Users with Teams Import (combined flat CSV — 3-step: Upload → Validate → Report)
// ---------------------------------------------------------------------------

interface UsersTeamsImportProps {
  canWrite: boolean;
}

/** Steps for the users-with-teams importer. */
const USERS_TEAMS_STEPS: { key: 'upload' | 'validate' | 'report'; label: string }[] = [
  { key: 'upload', label: 'Upload File' },
  { key: 'validate', label: 'Validate' },
  { key: 'report', label: 'Report' },
];

/**
 * Combined Users-with-Teams importer. Each row links a user to a team; from one
 * file it creates teams and users, assigns RBAC roles, adds team memberships
 * (with an in-team role), and marks team leads.
 *
 * Real 3-step flow:
 *   1. Upload  — choose the filled CSV.
 *   2. Validate — a dry-run preview (server writes nothing) showing new
 *      teams/users, unresolved RBAC roles, and rows that will be skipped.
 *   3. Report — after the user confirms, the import runs and the per-row
 *      created/updated/skipped outcome is shown.
 */
function UsersTeamsImport({ canWrite }: UsersTeamsImportProps) {
  const [step, setStep] = useState<'upload' | 'validate' | 'report'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const templateMutation = useDownloadUsersTeamsTemplate();
  const previewMutation = usePreviewUsersTeams();
  const importMutation = useImportUsersTeams();

  const preview: UsersTeamsPreview | undefined = previewMutation.data;
  const report: UsersTeamsImportReport | undefined = importMutation.data;
  const stepIndex = USERS_TEAMS_STEPS.findIndex((s) => s.key === step);

  const handleFile = useCallback(
    (f: File) => {
      setFile(f);
      // Uploading (choosing) a file immediately runs the dry-run validation.
      previewMutation.mutate(f, { onSuccess: () => setStep('validate') });
    },
    [previewMutation],
  );

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (!canWrite) return;
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  }

  function runImport() {
    if (!file) return;
    importMutation.mutate(file, { onSuccess: () => setStep('report') });
  }

  function reset() {
    setFile(null);
    setStep('upload');
    previewMutation.reset();
    importMutation.reset();
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Import Users with Teams
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            One row per user-in-a-team. Teams and users are created if missing, the RBAC role is
            assigned, the user is added to the team with their in-team role, and team leads are set.
          </p>
        </div>

        {/* Step indicator */}
        <nav aria-label="Users with teams progress">
          <ol className="flex flex-wrap items-center gap-2">
            {USERS_TEAMS_STEPS.map((s, idx) => (
              <li key={s.key} className="flex items-center">
                <span
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium',
                    idx < stepIndex
                      ? 'bg-success-500 text-white'
                      : idx === stepIndex
                        ? 'bg-brand-600 text-white'
                        : 'bg-surface-tertiary text-gray-600 dark:bg-surface-dark-tertiary dark:text-gray-300',
                  )}
                  aria-current={idx === stepIndex ? 'step' : undefined}
                >
                  {idx < stepIndex ? '✓' : idx + 1}
                </span>
                <span
                  className={cn(
                    'ml-2 text-sm',
                    idx === stepIndex
                      ? 'font-medium text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400',
                  )}
                >
                  {s.label}
                </span>
                {idx < USERS_TEAMS_STEPS.length - 1 && (
                  <span
                    className="mx-3 h-px w-8 bg-border dark:bg-border-dark"
                    aria-hidden="true"
                  />
                )}
              </li>
            ))}
          </ol>
        </nav>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                type="button"
                loading={templateMutation.isPending}
                onClick={() => templateMutation.mutate()}
              >
                {templateMutation.isPending ? 'Preparing…' : 'Download CSV template'}
              </Button>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Columns: team_name, user_name, user_email, user_role, job_role, team_job_role,
                is_team_lead.
              </span>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onClick={() => canWrite && fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-disabled={!canWrite}
              onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && canWrite) fileInputRef.current?.click();
              }}
              className={cn(
                'flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center transition-colors',
                isDragOver
                  ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
                  : 'border-border dark:border-border-dark',
                canWrite ? 'cursor-pointer' : 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={handleFileSelect}
                disabled={!canWrite}
              />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {previewMutation.isPending
                  ? 'Validating…'
                  : 'Drop your filled CSV here, or click to choose a file'}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">CSV up to 10MB</p>
            </div>

            {previewMutation.isError && (
              <div
                className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
                role="alert"
              >
                Could not validate file: {previewMutation.error?.message ?? 'Unknown error'}
              </div>
            )}
          </>
        )}

        {/* Step 2: Validate (dry-run preview) */}
        {step === 'validate' && preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="info">{preview.total_rows} rows</Badge>
              <Badge variant="success">{preview.teams_to_create} teams to create</Badge>
              <Badge variant="success">{preview.users_to_create} users to create</Badge>
              {preview.rows_with_unresolved_role > 0 && (
                <Badge variant="warning">
                  {preview.rows_with_unresolved_role} unresolved role(s)
                </Badge>
              )}
              {preview.warnings.length > 0 && (
                <Badge variant="danger">{preview.warnings.length} row(s) will be skipped</Badge>
              )}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Nothing has been imported yet. Review the plan below, then confirm.
            </p>

            <div className="max-h-80 overflow-auto rounded-lg border border-border dark:border-border-dark">
              <table className="min-w-full divide-y divide-border dark:divide-border-dark">
                <thead className="sticky top-0 bg-surface-tertiary dark:bg-surface-dark-tertiary">
                  <tr>
                    {['Row', 'Team', 'User', 'In-team role', 'RBAC role', 'Lead'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
                  {preview.rows.map((r) => (
                    <tr key={`${r.row}-${r.user}-${r.team}`}>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {r.row}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {r.team}{' '}
                        <Badge
                          variant={r.teamAction === 'create' ? 'success' : 'default'}
                          size="sm"
                        >
                          {r.teamAction === 'create' ? 'new' : 'existing'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {r.user}{' '}
                        <Badge
                          variant={r.userAction === 'create' ? 'success' : 'default'}
                          size="sm"
                        >
                          {r.userAction === 'create' ? 'new' : 'existing'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {r.teamJobRole}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {r.roleResolved ? (
                          <span className="text-gray-700 dark:text-gray-300">{r.roleName}</span>
                        ) : (
                          <Badge variant="warning" size="sm">
                            {r.roleName} — not found
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {r.isTeamLead ? 'Yes' : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview.warnings.length > 0 && (
              <div className="rounded-lg bg-warning-50 p-3 text-sm text-warning-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                <p className="font-medium">Rows that will be skipped (validation errors)</p>
                <ul className="mt-1 list-inside list-disc space-y-0.5">
                  {preview.warnings.map((w, i) => (
                    <li key={i}>
                      Row {w.row} ({w.field}): {w.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button variant="outline" size="sm" type="button" onClick={reset}>
                Back
              </Button>
              <Button
                size="sm"
                type="button"
                disabled={!canWrite}
                loading={importMutation.isPending}
                onClick={runImport}
              >
                {importMutation.isPending ? 'Importing…' : `Import ${preview.total_rows} row(s)`}
              </Button>
            </div>

            {importMutation.isError && (
              <div
                className="rounded-lg bg-danger-50 p-3 text-sm text-danger-700 dark:bg-red-900/30 dark:text-red-300"
                role="alert"
              >
                Import failed: {importMutation.error?.message ?? 'Unknown error'}
              </div>
            )}
          </div>
        )}

        {/* Step 3: Report */}
        {step === 'report' && report && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant="success">{report.imported} imported</Badge>
              <Badge variant="warning">{report.skipped} skipped</Badge>
              <Badge variant="info">{report.teams_created} teams created</Badge>
              <Badge variant="info">{report.users_created} users created</Badge>
            </div>

            <div className="overflow-hidden rounded-lg border border-border dark:border-border-dark">
              <table className="min-w-full divide-y divide-border dark:divide-border-dark">
                <thead className="bg-surface-tertiary dark:bg-surface-dark-tertiary">
                  <tr>
                    {['Row', 'Team', 'User', 'Result', 'Details'].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border bg-white dark:divide-border-dark dark:bg-surface-dark-secondary">
                  {report.results.map((r) => (
                    <tr key={`${r.row}-${r.user}-${r.team}`}>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {r.row}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {r.team}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {r.user}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {r.status === 'imported' ? (
                          <Badge variant="success">Imported</Badge>
                        ) : (
                          <Badge variant="warning">Skipped</Badge>
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                        {r.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Button variant="outline" size="sm" type="button" onClick={reset}>
              Import another file
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
